<!-- User2FA -->
<script lang="ts" setup>
import RadioButtonInput from "../../components/RadioButtonInput.vue"
import LoadingSection from "../../components/LoadingSection.vue"
import Icon from "../../components/Icon.vue"
import StarIcon from "../../components/Icons/StarIcon.vue"
import TextInput from "../../components/TextInput.vue"
import ActionButtonV2 from "../../components/ActionButtonV2.vue"
import Privilege from "../../components/Privilege.vue"
import AccessRestrictedMsg from "../../components/Messages/AccessRestrictedMsg.vue"
import { reactive, onMounted, computed, ref } from 'vue';
import { getQueryString } from '../../lib/QueryString';
import { ActionButtonStyle } from "../../lib/Types/Types";
import { TwoFactorTokenMode, TwoFactorEnforcementType, AppScreens, PrivilegeType, DatabaseIdTypes } from '../../Enums/ChoiceEnums';
import { DisplayStyle, IconTypes, MessageSectionType } from "../../Enums/UiEnums";
import { isValidEmail, isValidPhone } from "../../lib/Validation"
import * as Notification from '../../lib/Notification'
import * as ChoiceApi from "../../lib/ChoiceApi";
import "../../../css/choice.css";
import MessageBadge from "../../components/Messages/MessageBadge.vue"
import MessageSection from "../../components/Messages/MessageSection.vue"
import { prettyTime } from '../../lib/DateUtils';
import { IAuthenicatorSetupRespModel } from '../../lib/Api/Access';
import Card from "../../components/Card.vue"
import { TwoFactorAppInfoResp, UserTwoFactorInfo } from '../../lib/Api/User';
import { popAlert, confirmDialog } from "../../lib/Notification"
import { toInt } from '../../lib/Convert/String';
import { usePrivilegesStore } from '../../lib/Stores/PrivilegesStore';
import { initPinia } from '../../lib/PiniaMgr';
import { DatabaseId } from '../../lib/Models/DatabaseIdModel'
import { serverEnvironment } from '../../lib/Environment'

    const compData = reactive<{
        header?: string,
        securityUserId?: number,
        userId?: string,
        email?: string,
        phone?: string,
        tokenContact?: string,
        saveTimestamp?: string,
        twoFactorSetup?: TwoFactorSetupOptions,
        legacyTwoFactorInfo?: UserTwoFactorInfo,
        twoFactorAppInfo?: TwoFactorAppInfoResp,
        appEntryText?: string,   // Text to show in app to indicate which database
        authenciatorAppSetup?: IAuthenicatorSetupRespModel,
        allowEditing?: boolean,
        minimumTwoFactorEnforcementTypeForCompany: TwoFactorEnforcementType,
        loadingStatusItems:{
            legacyTwoFactorInfoLoaded: boolean,
            twoFactorAppInfoLoaded: boolean,
        }
    }>({
        minimumTwoFactorEnforcementTypeForCompany: TwoFactorEnforcementType.NotUsed,
        loadingStatusItems:
        {
            legacyTwoFactorInfoLoaded: false,
            twoFactorAppInfoLoaded: false
        }
    });

    const appEntryText = ref();
    const twoFactorMode = ref<TwoFactorTokenMode>();
    const privilegesStore = usePrivilegesStore(initPinia());

    enum TwoFactorSetupOptions
    {
        Setup,
        PromptAtNextLogin,
        RequireAtNextLogin
    }

    // Get the two factor mode for the UI to work with
    function computeTwoFactorMode(): TwoFactorTokenMode
    {
        if (TwoFactorEnforcementType.RequireConfirmation === compData.twoFactorAppInfo?.twoFactorAppEnforcementTypeSetting
            && compData.legacyTwoFactorInfo)
        {
            compData.legacyTwoFactorInfo.twoFactorMode = TwoFactorTokenMode.TwoFactorApp;
        }

        return compData.legacyTwoFactorInfo?.twoFactorMode ?? TwoFactorTokenMode.None;
    }

    onMounted(async () =>{
        compData.header = getQueryString("header");

        compData.securityUserId = toInt(getQueryString("securityUserId"));
        compData.userId = getQueryString("user_id");
        if (!compData.securityUserId && !compData.userId)
        {
            alert("Neither securityUserId nor userId provided in URL");
            return;
        }        

        compData.allowEditing = await privilegesStore.allowAll(AppScreens.User2FA);

        const default2FASettingForUsers = (await ChoiceApi.companySettings.getIntVal(ChoiceApi.companySettings.Setup.Default2FASettingForUsers));
        if (undefined != default2FASettingForUsers)
        {
            compData.minimumTwoFactorEnforcementTypeForCompany = <TwoFactorEnforcementType>default2FASettingForUsers;
        }
        await getTwoFactorInfo();
    });

    async function getTwoFactorInfo()
    {
        // Get the legacy and modern (app) two factor info in parallel
        getLegacyTwoFactorInfo().catch(() => console.log("Problem calling getLegacyTwoFactorInfo()"));
        getTwoFactorAppInfo().catch(() => console.log("Problem calling getTwoFactorAppInfo()"))
    }

    // Get the 'legacy' OfficeMD 2FA info
    async function getLegacyTwoFactorInfo()
    {
        const resp = await ChoiceApi.user.getLegacyTwoFactorInfo(compData.securityUserId, compData.userId);
        if (resp)
        {
            compData.legacyTwoFactorInfo = resp;
            twoFactorMode.value = computeTwoFactorMode();

            compData.userId = resp.userId;

            if (TwoFactorTokenMode.Email === resp.twoFactorMode)
                compData.email = resp.tokenContact
            if (TwoFactorTokenMode.Text === resp.twoFactorMode)
                compData.phone = resp.tokenContact;
        }
        compData.loadingStatusItems.legacyTwoFactorInfoLoaded = true;
    }

    // Get the 'modern' 2FA info
    async function getTwoFactorAppInfo()
    {
        let id: DatabaseId;
        if (compData.securityUserId)
            id = new DatabaseId(compData.securityUserId, DatabaseIdTypes.security_users_id);
        else if (compData.userId)
            id = new DatabaseId(compData.userId, DatabaseIdTypes.user_id);
        else
        {
            alert("Need either securityUserId or userID to be valid");
            return;
        }

        const resp = await ChoiceApi.user.getTwoFactorAppInfo(id);
        if (resp)
        {
            compData.twoFactorAppInfo = resp;
            twoFactorMode.value = computeTwoFactorMode();
        
            // For bang users, indicate the server environment as the suggested authenticator application entry name for OfficeMD
            if (isBangUser.value)
            {
                if (compData.twoFactorAppInfo)
                    compData.twoFactorAppInfo.suggestedAppEntryText = serverEnvironment().toString();
            }
                
            compData.appEntryText = compData.twoFactorAppInfo?.suggestedAppEntryText;
        }
        
        // Make sure to set the two factor value
        setTwoFactorSetupValueForUI();
        compData.loadingStatusItems.twoFactorAppInfoLoaded = true;
    }

    const isBangUser = computed(() => compData.userId?.startsWith("!"));

    const isLoading = computed(() =>{
        if (!privilegesStore.screens[AppScreens.User2FA])
            return true;

        if (privilegesStore.screens[AppScreens.User2FA] >= PrivilegeType.Read)
        {
            if (compData.loadingStatusItems.legacyTwoFactorInfoLoaded && compData.loadingStatusItems.twoFactorAppInfoLoaded)
                return false;
            return true;
        }
        return false;
    });

    const tokenContact = computed<string|undefined>(() => {
        if (TwoFactorTokenMode.Email === twoFactorMode.value)
            return compData.email;
        if (TwoFactorTokenMode.Text === twoFactorMode.value)
            return compData.phone;
    });

    async function send2FAToken()
    {
        if (undefined === validLegacySetupObject.value || !validLegacySetupObject.value.tokenContact)
        {
            alert("Missing information needed to send 2FA token");
            return;
        }

        const resp = await ChoiceApi.user.sendTwoFactorToken(validLegacySetupObject.value.userId, validLegacySetupObject.value.twoFactorMode, validLegacySetupObject.value.tokenContact);
        if (!resp)
            return;
        await Notification.popAlert("Sent token");
    }

    async function save2FASetting()
    {
        if (undefined === validLegacySetupObject.value)
        {
            alert("Missing information needed to send 2FA token");
            return;
        }
        if (!compData.userId)
        {
            alert("compData.userId is undefined");
            return;
        }

        if (TwoFactorTokenMode.TwoFactorApp === validLegacySetupObject.value.twoFactorMode)
        {
            switch(compData.twoFactorSetup){
                case TwoFactorSetupOptions.RequireAtNextLogin:
                    await ChoiceApi.user.saveTwoFactorAppInfo(compData.userId, TwoFactorEnforcementType.RequireKey);
                    break;
                case TwoFactorSetupOptions.PromptAtNextLogin:
                    await ChoiceApi.user.saveTwoFactorAppInfo(compData.userId, TwoFactorEnforcementType.PromptKey);
                    break;

                case undefined:
                    await Notification.popAlert("Please select a Two Factor setup mode");
                    return;
            }
        }
        else
        {
            // Save legacy data
            const resp = await ChoiceApi.user.saveLegacyTwoFactorSettings(validLegacySetupObject.value.userId, validLegacySetupObject.value.twoFactorMode, validLegacySetupObject.value.tokenContact);
            if (!resp)
            {
                await Notification.popAlert("Problem saving");
                return;
            }
            if (resp.tokenSent)
                await Notification.popAlert("Sent new token");

            // Update 2FA application settings
            await ChoiceApi.user.saveTwoFactorAppInfo(compData.userId, TwoFactorEnforcementType.NotUsed);

            // Clear which setup is selected
            compData.twoFactorSetup = undefined;
        }

        // Update the local data to clear any left over 2FA app info
        compData.twoFactorAppInfo = undefined;
        await getTwoFactorInfo();

        compData.saveTimestamp = prettyTime(new Date());
    }

    const validLegacySetupObject = computed<{userId: string, twoFactorMode: TwoFactorTokenMode, tokenContact?: string}|undefined>(() => 
    {
        if (undefined === compData.userId || undefined === twoFactorMode.value)
            return;
        if ((!isValidEmail(tokenContact.value) && TwoFactorTokenMode.Email === twoFactorMode.value)
            || (!isValidPhone(tokenContact.value) && TwoFactorTokenMode.Text === twoFactorMode.value)
            || (undefined === compData.twoFactorSetup && TwoFactorTokenMode.TwoFactorApp === twoFactorMode.value))
            return;
        return { userId: compData.userId, twoFactorMode: twoFactorMode.value, tokenContact: tokenContact.value };
    });

    async function loadAuthenticatorSetup()
    {
        if (!compData.twoFactorAppInfo)
        {
            alert("compData.twoFactorAppInfo is null");
            return;
        }
        if (!compData.appEntryText || compData.appEntryText.trim().length < 3)
        {
            await Notification.popAlert("Please provide a valid <b>Title for Authenticator</b> value");
            appEntryText.value?.focus();
            return;
        }

        if (!compData.userId?.length)
        {
            alert("User ID is invalid");
            return;
        }

        compData.authenciatorAppSetup = await ChoiceApi.user.getAuthenticatorSetup(compData.twoFactorAppInfo?.proposedEncryptedTwoFactorKey, compData.appEntryText, compData.userId);
        if (!compData.authenciatorAppSetup)
        {
            await Notification.popAlert("Error fetching Authenticator application setup");
        }
    }

    // Transfer 2FA setup info a reactive variable
    function setTwoFactorSetupValueForUI(): TwoFactorSetupOptions|undefined
    {
        if (!compData.twoFactorAppInfo)
            return;

        switch(compData.twoFactorAppInfo?.twoFactorAppEnforcementTypeSetting)
        {
            case TwoFactorEnforcementType.NotUsed:
            compData.twoFactorSetup = undefined;
            break;

            case TwoFactorEnforcementType.PromptKey:
            compData.twoFactorSetup = TwoFactorSetupOptions.PromptAtNextLogin;
            break;

            case TwoFactorEnforcementType.RequireKey:
            compData.twoFactorSetup = TwoFactorSetupOptions.Setup;
            break;

            case TwoFactorEnforcementType.RequireConfirmation:
            compData.twoFactorSetup = TwoFactorSetupOptions.RequireAtNextLogin;
            break;
        }
    }

    async function changeToTwoFactorAuth(newTwoFactorSetupValue: TwoFactorSetupOptions)
    {
        compData.twoFactorSetup = newTwoFactorSetupValue;

        
        if (!compData.twoFactorAppInfo)
        {
            alert("compData.twoFactorAppInfo is undefined");
            return;
        }
        if (!compData.userId)
        {
            alert("compData.userId is undefined");
            return;            
        }
        if (!compData.securityUserId)
        {
            alert("compData.securityUserId is invalid");
            return;            
        }


        if (!compData.twoFactorSetup)
            setTwoFactorSetupValueForUI();

        // Ask to update settings
        if (compData.twoFactorAppInfo.twoFactorAppEnforcementTypeSetting !== TwoFactorEnforcementType.RequireConfirmation)
        {
            compData.twoFactorSetup = TwoFactorSetupOptions.Setup;

            if (!await confirmDialog("Before you setup the authenticator application, we need to update the user settings to require the use of the application at the next login.  Please confirm."))
                return;

            // Save the 2FA settings
            const resp = await ChoiceApi.user.saveTwoFactorAppInfo(compData.userId, TwoFactorEnforcementType.RequireKey);
            if (!resp?.successful)
            {
                await popAlert(`Error setting two factor application data: ${resp?.statusDescription ?? "Unknown error"}`);
                return;
            }

            // Update the app info
            compData.twoFactorAppInfo = await ChoiceApi.user.getTwoFactorAppInfo({ id: compData.securityUserId, idType: DatabaseIdTypes.security_users_id  }  );
        }
    }

    const showTwoFactorAppSetupCard = computed(() => 
        TwoFactorEnforcementType.RequireConfirmation === compData.twoFactorAppInfo?.twoFactorAppEnforcementTypeSetting);

    const showSaveButton = computed(() => 
    {
        if (twoFactorMode.value === TwoFactorTokenMode.TwoFactorApp)
            return TwoFactorEnforcementType.RequireConfirmation !== compData.twoFactorAppInfo?.twoFactorAppEnforcementTypeSetting;
        return true;
    });

    const allow2FAPromptOption = computed(() => 
        compData.minimumTwoFactorEnforcementTypeForCompany === TwoFactorEnforcementType.NotUsed
        || compData.minimumTwoFactorEnforcementTypeForCompany === TwoFactorEnforcementType.PromptKey);

    const minimumTwoFactorEnforcementTypeNameForCompany = computed<string|undefined>(()=>{
        // Don't show once we're using 2FA app
        if (TwoFactorTokenMode.TwoFactorApp == twoFactorMode.value)
            return;

        switch(compData.minimumTwoFactorEnforcementTypeForCompany)
        {
            case TwoFactorEnforcementType.PromptKey:
                return "Will be prompted to setup 2FA application at next login";
            case TwoFactorEnforcementType.RequireKey:
                return "Will be required to setup 2FA application at next login"
        }
    });

    const twoFactorEnforcementName = computed<string|undefined>(()=> {
        switch (compData.twoFactorAppInfo?.twoFactorAppEnforcementTypeSetting)
        {
            case TwoFactorEnforcementType.PromptKey:
            return "Prompt to change at next login";

            case TwoFactorEnforcementType.RequireKey:
            return "Required to change at next login";
        }
    });
</script>

<template>
    <Privilege :screen="AppScreens.User2FA" :privilege="PrivilegeType.Read">
        <LoadingSection :isLoading="isLoading" loadingMsg="Loading user auth info..." />
        <div v-if="!isLoading">

            <RadioButtonInput :value="TwoFactorTokenMode.None" v-model="twoFactorMode" :disabled="!compData.allowEditing">
                None
                <StarIcon v-if="compData.legacyTwoFactorInfo?.twoFactorMode === TwoFactorTokenMode.None" title="No 2FA currently enabled"/>
            </RadioButtonInput>

            <RadioButtonInput :value="TwoFactorTokenMode.Email" v-model="twoFactorMode" :disabled="!compData.allowEditing">
                Email
                <StarIcon v-if="compData.legacyTwoFactorInfo?.twoFactorMode === TwoFactorTokenMode.Email" title="Email 2FA currently enabled"/>
            </RadioButtonInput>

            <div v-if="TwoFactorTokenMode.Email == twoFactorMode" class="row">
                <div class="row">                    
                    <div class="col-sm-4 ms-4">
                        <TextInput v-model:textValue="compData.email" :validator="(val) => isValidEmail(val)" :required="true">
                            <template #LeftAddon>
                                <Icon :icon="IconTypes.Email" />
                            </template>
                        </TextInput>
                    </div>
                    <div class="col-sm-4">
                        <ActionButtonV2 :action="ActionButtonStyle.Send" text="Send Token" :disabled="!isValidEmail(compData.email)" :performAction="send2FAToken" />
                    </div>
                </div>
            </div>
        
            <RadioButtonInput :value="TwoFactorTokenMode.Text" v-model="twoFactorMode" :disabled="!compData.allowEditing">
                Text
                <StarIcon v-if="compData.legacyTwoFactorInfo?.twoFactorMode === TwoFactorTokenMode.Text" title="Text Message 2FA currently enabled"/>
            </RadioButtonInput>
            

            <div v-if="TwoFactorTokenMode.Text == twoFactorMode" class="ms-4">
                <div class="row">
                    <div class="col-sm-4">
                        <TextInput v-model:textValue="compData.phone" :validator="(val) => isValidPhone(val)" :required="true">
                            <template #LeftAddon>
                                <Icon :icon="IconTypes.Phone" />
                            </template>
                        </TextInput>
                    </div>
    
                    <div class="col-sm-4">
                        <ActionButtonV2 :action="ActionButtonStyle.Send" text="Send Token" :disabled="!isValidPhone(compData.phone)" :performAction="send2FAToken" />
                    </div>
                </div>
            </div>
        
            <RadioButtonInput :value="TwoFactorTokenMode.IdenTrust" v-model="twoFactorMode" :disabled="!compData.allowEditing">
                IdenTrust
                <StarIcon v-if="compData.legacyTwoFactorInfo?.twoFactorMode === TwoFactorTokenMode.IdenTrust" title="IdenTrust 2FA currently enabled"/>
            </RadioButtonInput>    
    
            <!-- 2FA application setting -->
            <RadioButtonInput :value="TwoFactorTokenMode.TwoFactorApp" v-model="twoFactorMode" :disabled="!compData.allowEditing">
                Two Factor Application
                <StarIcon v-if="compData.legacyTwoFactorInfo?.twoFactorMode === TwoFactorTokenMode.TwoFactorApp" title="Two Factor Application currently enabled"/>

                <!-- Company/Database Minimum 2FA -->
                <MessageBadge v-if="minimumTwoFactorEnforcementTypeNameForCompany" title="Database configuration set to migrate login to using a 2FA application" :displayStyle="DisplayStyle.Info">
                    {{minimumTwoFactorEnforcementTypeNameForCompany}}
                </MessageBadge>

                <!-- User Minimum 2FA -->
                <MessageBadge v-if="TwoFactorTokenMode.TwoFactorApp !== twoFactorMode">
                    {{ twoFactorEnforcementName }}
                </MessageBadge>

            </RadioButtonInput>
            <div v-if="TwoFactorTokenMode.TwoFactorApp == twoFactorMode" class="ms-4">
                <RadioButtonInput :value="TwoFactorSetupOptions.Setup" v-model="compData.twoFactorSetup" @update:model-value="async val => await changeToTwoFactorAuth(val)" :disabled="!compData.allowEditing">
                    Setup the 2FA application
                </RadioButtonInput>
    
                <!-- Don't show once 2FA is setup -->
                <div v-if="TwoFactorSetupOptions.Setup == compData.twoFactorSetup" class="ps-4">
                    <!-- Show the the Application setup card -->
                    <Card class="mb-4" v-if="showTwoFactorAppSetupCard">
                        <template #title>
                            Setup 2FA Authenticator
                        </template>
                        Title for Authenticator
                        <div class="row mb-1">
                            <div class="col-sm-3">
                                <TextInput ref="appEntryText" v-model:text-value="compData.appEntryText" @update:text-value="newVal => {compData.authenciatorAppSetup = undefined; compData.appEntryText = (newVal as string|undefined); } " :validator="val => val.length >= 3" />
                            </div>
                            <div class="col-sm-9">
                                <button class="btn btn-info me-1" title="Use database name for Authentication application" 
                                        v-if="compData.twoFactorAppInfo?.suggestedAppEntryText.toLowerCase() != compData.appEntryText?.toLowerCase()"
                                        @click="() => compData.appEntryText = compData.twoFactorAppInfo?.suggestedAppEntryText">
                                    {{compData.twoFactorAppInfo?.suggestedAppEntryText}}
                                </button>
                                <ActionButtonV2 :action="ActionButtonStyle.Blank" text="Show Setup Info" :perform-action="loadAuthenticatorSetup"/>
                            </div>
                        </div>
    
                        <div class="mt-3" v-if="compData.authenciatorAppSetup">
                            <div>
                                <div>
                                    Scan QR Code with Mobile Device:
                                </div>        
                                <img :src="compData.authenciatorAppSetup.qrCodeSetupImageUrl" alt="2FA QR Code" />
                            </div>
                    
                            <div>
                                <div>
                                    Manual Entry Key:
                                </div>
                                <div class="ms-2">
                                    {{compData.authenciatorAppSetup.manualEntryKey}}
                                </div>
                            </div>
                        </div>
                    </Card>
                    <MessageSection :display-style="DisplayStyle.Danger" v-if="!showTwoFactorAppSetupCard">
                        <div>
                            The application cannot be setup until you update the user settings to indicate the user will be required to use a two factor authentication application.
                        </div>
                        <ActionButtonV2 
                            :action="ActionButtonStyle.Setting" 
                            :perform-action="async () => await changeToTwoFactorAuth(TwoFactorSetupOptions.Setup)">
                                Update User Setting
                        </ActionButtonV2>
                    </MessageSection>      
                </div>
    
                <template v-if="TwoFactorEnforcementType.RequireConfirmation !== compData.twoFactorAppInfo?.twoFactorAppEnforcementTypeSetting">
                    <RadioButtonInput :value="TwoFactorSetupOptions.PromptAtNextLogin" v-model="compData.twoFactorSetup" :disabled="!compData.allowEditing || !allow2FAPromptOption">
                        Prompt the user at login to move to a 2FA application the next time they login
                    </RadioButtonInput>
    
                    <RadioButtonInput :value="TwoFactorSetupOptions.RequireAtNextLogin" v-model="compData.twoFactorSetup" :disabled="!compData.allowEditing">
                        Require the user to setup a 2FA application the next time they login
                    </RadioButtonInput>
                </template>
    
                <MessageSection :display-style="DisplayStyle.Info" :type="MessageSectionType.Row">
                    You can use an application that uses the <em>Time-based One-time Password Algorithm (TOTP)</em>.
                    Examples of this are 
                    <ul>
                        <li>Google Authenticator</li>
                        <li>ID.me Authenticator</li>
                        <li>Microsoft Authenticator</li>
                    </ul>
                </MessageSection>
            </div>
    
            <div class="mt-2">
                <div v-if="compData.saveTimestamp" >
                    <MessageBadge :displayStyle="DisplayStyle.Secondary" class="mb-2">
                        Saved at {{ compData.saveTimestamp }}
                    </MessageBadge>
                </div>
    
                <Privilege :screen="AppScreens.User2FA" :privilege="PrivilegeType.ReadUpdateWrite" >
                    <ActionButtonV2 
                        v-if="showSaveButton"
                        :action="ActionButtonStyle.Save" 
                        :displayStyle="DisplayStyle.Success" 
                        :performAction="save2FASetting"
                        :disabled="undefined === validLegacySetupObject"/>
                        <template #notAllowed="{rights}">
                            <AccessRestrictedMsg :rights="rights" />
                        </template>
                </Privilege>
    
            </div>
        </div>
        <template #notAllowed="{rights}">
            <AccessRestrictedMsg :rights="rights" />
        </template>
    </Privilege>

</template>